import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MODULI, moduloDaPercorso } from "../moduli";
import { useApp } from "../AppContext";
import { useAuth } from "../AuthContext";
import {
  scaricaBytesPdfGrezzo, anteprimaCalibrazionePdf, salvaModelloPdf, applicaModelloPdf, getModelloPdf,
  getVocabolarioColonnePdf,
} from "../api";
import DivisioneModal from "../DivisioneModal";
import EstrazioneModal from "../EstrazioneModal";

// Calibrazione manuale di un PDF: un PDF non ha colonne vere come un Excel,
// quindi non c'è modo affidabile di indovinare da soli "questo è il codice
// articolo" — è l'utente che lo dice, cliccando sul frammento di testo
// giusto direttamente sulla pagina. Il risultato (dove sono le cose) si
// salva come "modello" riutilizzabile per quel cliente: i PDF futuri con lo
// stesso layout non richiedono di rifare questo passaggio.
//
// Due tipi di campo da definire cliccando:
//  - "Campo di pagina": un valore che si ripete identico su tutta la pagina
//    (es. il codice articolo in cima al foglio) — si cerca, pagina per
//    pagina, il frammento più vicino a quella posizione.
//  - "Colonna tabella": una colonna della tabella materiali che si ripete
//    riga per riga — si clicca sulla cella di intestazione, la sua
//    posizione X diventa il confine della colonna.
export default function CalibrazionePdf() {
  const navigate = useNavigate();
  const location = useLocation();
  // Import Ordini è una pagina unica (upload + template + mapping insieme),
  // non condivide gli stati Home/Mapping di diba/ddt: niente tracciato da
  // caricare qui, e al salvataggio si torna dritti su quella pagina invece
  // che su una Home con lo step "scegli come mappare".
  const inOrdini = location.pathname.startsWith("/ordini");
  const modulo = inOrdini ? "ordini" : moduloDaPercorso(location.pathname);
  const percorsoBase = inOrdini ? "/ordini" : MODULI[modulo].percorsoBase;
  const {
    setFileCaricato, setTemplateAttivo, setMapping, setColonneNascoste, setRigheMappate, setRisultatoPdfOrdini,
  } = useApp();
  const { puoMappare } = useAuth();

  // Stessa guardia difensiva di Mapping.jsx: chi non può mappare non deve
  // poter arrivare qui, in nessun modo.
  useEffect(() => {
    if (!puoMappare) navigate(percorsoBase);
  }, [puoMappare, navigate, percorsoBase]);

  const parametri = new URLSearchParams(location.search);
  const pdfId = parametri.get("pdfId");
  const modelloIdInModifica = parametri.get("modelloId"); // presente solo se si sta modificando un modello già salvato

  const canvasRef = useRef(null);
  const filaRenderRef = useRef(Promise.resolve()); // coda dei render pdfjs sul canvas: mai due insieme, altrimenti pdfjs lancia un errore
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numeroPagina, setNumeroPagina] = useState(1);
  const [totalePagine, setTotalePagine] = useState(1);
  const [frammenti, setFrammenti] = useState([]);
  const [scala, setScala] = useState(1);
  const [altezzaPagina, setAltezzaPagina] = useState(0);
  const [zoomManuale, setZoomManuale] = useState(1); // moltiplicatore aggiuntivo: ingrandisce tutto il foglio, non solo un ritaglio

  const [modalita, setModalita] = useState("colonna"); // "campo" | "colonna"
  const [campiPagina, setCampiPagina] = useState([]); // [{ nome, x, y }]
  const [colonneTabella, setColonneTabella] = useState([]); // [{ nome, x, y }]
  const [colonnaAncora, setColonnaAncora] = useState("");
  const [popup, setPopup] = useState(null); // { frammento, top, left }
  const [nomeCampo, setNomeCampo] = useState("");

  // Alcuni PDF stampano la stessa tabella su due pagine affiancate (le
  // colonne che non entrano in larghezza finiscono su una pagina a parte) o
  // intercalano pagine di riepilogo: mapparle darebbe righe doppie e
  // incomplete. Si escludono sfogliandole, con l'interruttore sulla pagina
  // che si sta guardando.
  const [pagineEscluse, setPagineEscluse] = useState([]); // [2, 4, 6]

  // Tra le righe di materiale se ne intrufolano di non-materiali (titoli di
  // sezione, contatori "Displaying 1 - 6 of 19"): hanno solo la prima
  // colonna compilata. Si escludono cliccandole DIRETTAMENTE sulla pagina
  // (modalità "Escludi riga", terza opzione accanto a colonna/campo): il
  // programma guarda quale colonna già definita è vuota in quella riga ma
  // compilata nelle altre, e la marca come "obbligatoria" — le righe senza
  // quella colonna cadono da sole, anche sui PDF futuri con titoli diversi.
  const [colonneObbligatorie, setColonneObbligatorie] = useState([]); // ["Product", ...]

  // Alcuni PDF hanno più tabelle impilate sulla stessa pagina (es. sotto la
  // tabella che interessa ce n'è un'altra, con colonne diverse ma che
  // condivide per caso qualche posizione X con la colonna ancora): senza un
  // limite, le sue righe rischiano di essere lette come righe buone. Si
  // marca dove finisce l'area utile cliccando la prima riga DA NON
  // considerare (modalità "Limite area tabella") — tutto quello che sta a
  // quella altezza o più in basso, su ogni pagina, resta fuori. Un solo
  // numero (non un rettangolo): il limite superiore è già l'intestazione
  // della tabella stessa (colonnaAncora/colonneTabella), definita a parte.
  const [limiteInferioreTabella, setLimiteInferioreTabella] = useState(null); // numero (coordinata Y del PDF) o null

  // Alcuni PDF (es. un ordine con sotto ogni riga l'elenco dei componenti/
  // distinta base) infilano tra una riga vera e la successiva un blocco di
  // testo da scartare la cui altezza CAMBIA ogni volta (dipende da quante
  // righe va a capo la descrizione sopra): un limite fisso come sopra non
  // basta. Qui invece si marca il TESTO che segna l'inizio del blocco da
  // buttare (modalità "Fine riga"), cliccandolo direttamente sulla pagina:
  // appena ricompare (anche a un'altezza diversa, anche più volte nello
  // stesso documento), tutto da lì fino alla prossima riga vera viene
  // scartato.
  const [testoFineRiga, setTestoFineRiga] = useState(null); // stringa (testo esatto del frammento) o null

  // Tolleranza (punti PDF) entro cui due frammenti alla stessa "altezza di
  // stampa" contano come sulla stessa riga visiva — di norma 2pt bastano.
  // Alcuni PDF però stampano una colonna (tipicamente un totale allineato a
  // destra, lontano dal resto della riga) con un piccolo scarto verticale
  // (1-3pt) dal resto della riga: se quella colonna è anche la colonna
  // ancora, lo scarto la fa cadere in una riga visiva separata e la riga
  // logica si spezza nel punto sbagliato (visto su un file Sanser). null =
  // usa il default del motore (2pt), invariato per tutti i modelli già
  // calibrati che non hanno mai impostato questo campo.
  const [tolleranzaRigaVisiva, setTolleranzaRigaVisiva] = useState(null);

  // Blocco taglie dinamico PER RIGA (routes/pdfEstrattore.js,
  // estraiTaglieRigaDinamiche): alcuni ordini stampano, dentro ogni riga
  // articolo, una mini-tabella taglie/qtà il cui numero di colonne cambia
  // riga per riga (non solo pagina per pagina come "taglia dinamica" sulle
  // colonne — vedi alternaTaglia): niente posizione X in comune tra le
  // taglie di una riga e quelle della successiva, quindi si abbinano per
  // ORDINE ORIZZONTALE (etichette vs valori), non per X. null = disattivato,
  // invariato per tutti i modelli già calibrati.
  const [taglieRigaDinamiche, setTaglieRigaDinamiche] = useState(null); // { offsetEtichette, offsetValori, escludiTesto } o null

  // "Unisci campi": alcuni valori veri non sono un campo unico ma
  // l'accostamento di uno già di pagina e uno già di tabella (es. il vero
  // numero ordine cliente/commessa = numero d'ordine scritto in copertina +
  // numero di riga letto dalla tabella materiali). Qui non c'è un click
  // sulla pagina da fare (i due campi sorgente sono già stati calibrati a
  // parte): è solo un piccolo form che sceglie due (o più) nomi già
  // definiti, un separatore, e il nome del campo nuovo che ne risulta.
  const [campiCombinati, setCampiCombinati] = useState([]); // [{ nome, sorgenti: ["A","B"], separatore }]
  const [nuovaCombinazione, setNuovaCombinazione] = useState({ nome: "", sorgenti: ["", ""], separatore: "-" });

  // "Colonna variante": alcune distinte colorate hanno, oltre alle colonne
  // materiale normali, una o più colonne il cui valore per riga è il colore
  // da usare per QUELLA variante/combinazione del prodotto finito — la loro
  // intestazione (es. "F0JZH - NERO ARGENTO") è il nome della variante
  // stessa. Un click, in questa modalità, sull'intestazione di una di quelle
  // colonne la crea già pronta (nome automatico "Variante 1", "Variante 2",
  // ... — il testo vero dell'intestazione non serve come nome, serve solo
  // come VALORE letto dopo): ogni riga materiale genera poi tante righe
  // quante sono le colonne variante — una per ciascuna, col valore della
  // colonna nel campo "valore" e il testo dell'intestazione cliccata nel
  // campo "intestazione" — così la successiva suddivisione in file per
  // combinazione (già esistente in Revisione.jsx) fa il resto da sola. Vedi
  // routes/pdfEstrattore.js (variantiOrizzontali).
  const [variantiOrizzontali, setVariantiOrizzontali] = useState({ campoValore: "Colore Materiale", campoIntestazione: "Nome Variante" });

  // "Nome posizione": alcune distinte hanno righe-titolo di sezione
  // ("Material (1)", "Internal Materials (2)", "Components (6)") che oggi si
  // scartano — invece ognuna dà il nome posizione alle righe materiale che la
  // seguono ("Material 1", "Internal Materials 1", "Internal Materials 2"...),
  // con un contatore che riparte a ogni titolo. Si clicca una di quelle
  // righe-titolo direttamente sul foglio (modalità "Nome posizione") e si dà
  // il nome della colonna che ne risulta — vedi routes/pdfEstrattore.js
  // (anagraficaPosizioni).
  const [anagraficaPosizioni, setAnagraficaPosizioni] = useState(null); // { x, y, nomeColonna, separatore } o null

  // "Riconosci le colonne": nomi di colonna/campo già usati negli altri
  // modelli PDF di questo modulo. Quando dalla riga di intestazione del PDF
  // si ricava una colonna, se il suo testo combacia (a meno di maiuscole/
  // accenti/punteggiatura) con uno di questi, si riusa quel nome — così i
  // modelli restano coerenti. Serve anche per l'autocomplete dei nomi.
  const [vocabolario, setVocabolario] = useState([]); // [{ nome, usato }]
  const [messaggioRiconoscimento, setMessaggioRiconoscimento] = useState("");

  // "Dividi in colonne": quando due colonne vicine nel PDF si fondono in una
  // sola (es. "UM" e "Supplier" troppo ravvicinate), invece di dover
  // ricalibrare al pixel si marca la colonna già definita come "da dividere"
  // — stessa interfaccia a pillole cliccabili dell'estrazione Excel (vedi
  // EstrazioneModal.jsx), applicata a un valore vero letto da questa stessa
  // pagina. Motore in routes/pdfEstrattore.js.
  const [colonnaInDivisione, setColonnaInDivisione] = useState(null); // colonna (oggetto) o null

  // "Estrai una parte": a differenza di "dividi" (due parti, ENTRAMBE
  // utili) qui serve solo UN valore preso da dentro una stringa più lunga,
  // il resto si butta — es. isolare "641D20A3221" da dentro
  // "DIOR COLLECTION > ... > 641D20A3221 - RS-ROBE ... > Style BOM". Stesso
  // strumento a pillole già usato in Mapping.jsx per i file Excel/DDT (vedi
  // EstrazioneModal.jsx), qui applicato a un valore letto dal PDF invece
  // che da un file caricato.
  const [campoInEstrazione, setCampoInEstrazione] = useState(null); // campo (oggetto) o null

  const [anteprima, setAnteprima] = useState(null);
  const [calcolando, setCalcolando] = useState(false);
  const [nomeCliente, setNomeCliente] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    if (!pdfId) { setErrore("Nessun PDF da calibrare: torna a Home e ricarica il file."); return; }
    let annullato = false;
    (async () => {
      try {
        const bytes = await scaricaBytesPdfGrezzo(pdfId);
        const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf");
        pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
        const doc = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (annullato) return;
        setPdfDoc(doc);
        setTotalePagine(doc.numPages);
      } catch (err) {
        if (!annullato) setErrore("Impossibile aprire il PDF: " + err.message);
      }
    })();
    return () => { annullato = true; };
  }, [pdfId]);

  useEffect(() => {
    let annullato = false;
    getVocabolarioColonnePdf(modulo)
      .then(({ nomi }) => { if (!annullato) setVocabolario(nomi || []); })
      .catch(() => { if (!annullato) setVocabolario([]); });
    return () => { annullato = true; };
  }, [modulo]);

  useEffect(() => {
    if (!pdfDoc) return;
    let annullato = false;
    // Ogni cambio di pagina o di zoom mette in coda un nuovo render sullo
    // stesso <canvas>: incodandolo dopo l'ultimo già in corso (invece di
    // lanciarlo subito) si evita che due render si sovrappongano sullo
    // stesso canvas, cosa che pdfjs non permette e segnala con un errore.
    filaRenderRef.current = filaRenderRef.current.catch(() => {}).then(async () => {
      if (annullato) return;
      const page = await pdfDoc.getPage(numeroPagina);
      const viewportBase = page.getViewport({ scale: 1 });
      const s = Math.min(1000 / viewportBase.width, 1.3) * zoomManuale;
      const viewport = page.getViewport({ scale: s });
      const canvas = canvasRef.current;
      if (!canvas || annullato) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;
      if (annullato) return;
      const content = await page.getTextContent();
      const items = content.items
        .filter((it) => it.str.trim() !== "")
        .map((it) => ({
          testo: it.str, x: it.transform[4], y: it.transform[5],
          larghezza: it.width, altezza: it.height || Math.abs(it.transform[3]) || 10,
        }));
      setFrammenti(items);
      setScala(s);
      setAltezzaPagina(viewportBase.height);
      setPopup(null);
    });
    return () => { annullato = true; };
  }, [pdfDoc, numeroPagina, zoomManuale]);

  // Modifica di un modello già salvato (arrivato da Home > "Modifica
  // modello"): precarica la sua calibrazione, così l'utente parte da quella
  // invece che da zero e la aggiusta guardando questo stesso PDF.
  useEffect(() => {
    if (!modelloIdInModifica) return;
    (async () => {
      try {
        const modello = await getModelloPdf(modelloIdInModifica);
        setCampiPagina(modello.calibrazione.campiPagina || []);
        setColonneTabella(modello.calibrazione.colonneTabella || []);
        setColonnaAncora(modello.calibrazione.colonnaAncora || "");
        setPagineEscluse(modello.calibrazione.pagineEscluse || []);
        setColonneObbligatorie(modello.calibrazione.colonneObbligatorie || []);
        setLimiteInferioreTabella(
          Number.isFinite(modello.calibrazione.limiteInferioreTabella) ? modello.calibrazione.limiteInferioreTabella : null
        );
        setTestoFineRiga(modello.calibrazione.testoFineRiga || null);
        setTolleranzaRigaVisiva(
          Number.isFinite(modello.calibrazione.tolleranzaRigaVisiva) ? modello.calibrazione.tolleranzaRigaVisiva : null
        );
        setTaglieRigaDinamiche(modello.calibrazione.taglieRigaDinamiche || null);
        setCampiCombinati(modello.calibrazione.campiCombinati || []);
        setAnagraficaPosizioni(modello.calibrazione.anagraficaPosizioni || null);
        if (modello.calibrazione.variantiOrizzontali) setVariantiOrizzontali(modello.calibrazione.variantiOrizzontali);
        setNomeCliente(modello.cliente || "");
      } catch (err) {
        setErrore("Impossibile caricare il modello da modificare: " + err.message);
      }
    })();
  }, [modelloIdInModifica]);

  // Suggerisce automaticamente la prima colonna definita come "ancora" (la
  // colonna che segna l'inizio di una nuova riga materiale) finché l'utente
  // non ne sceglie una diversa.
  useEffect(() => {
    if (!colonnaAncora && colonneTabella.length > 0) setColonnaAncora(colonneTabella[0].nome);
  }, [colonneTabella, colonnaAncora]);

  // Click su un frammento in modalità "Limite area tabella": quel frammento
  // e tutto ciò che sta alla stessa altezza o più in basso (su ogni pagina)
  // non verrà più considerato per le righe della tabella — vedi
  // calcolaRigheLogichePagina più sotto e routes/pdfEstrattore.js.
  function impostaLimiteInferiore(frammento) {
    setLimiteInferioreTabella(frammento.y);
  }

  // Click su un frammento in modalità "Fine riga": il suo TESTO (non la
  // posizione) diventa il marcatore — vedi calcolaRigheLogichePagina più
  // sotto e routes/pdfEstrattore.js.
  function impostaTestoFineRiga(frammento) {
    setTestoFineRiga(frammento.testo.trim());
  }

  function normalizzaNome(s) {
    return String(s || "")
      .toLowerCase()
      .normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  // Mappa "nome normalizzato -> nome canonico già usato altrove", per riusare
  // la stessa dicitura tra modelli (es. "Description" -> "description" se in
  // un altro modello quella colonna si chiama così).
  const vocabolarioMappa = useMemo(() => {
    const m = new Map();
    for (const v of vocabolario) {
      const k = normalizzaNome(v.nome);
      if (k && !m.has(k)) m.set(k, v.nome);
    }
    return m;
  }, [vocabolario]);

  // Nomi noti di colonna "ancora" (segna l'inizio di ogni riga): serve per
  // indovinare quale delle colonne riconosciute proporre come ancora.
  const ANCORE_NOTE = new Set([
    "sequence", "seq", "riga", "row", "line", "codice", "codice articolo", "articolo",
    "article", "colore", "color", "lvl", "livello", "level", "numero", "pos", "posizione",
  ]);

  // Click su un frammento in modalità "Riconosci colonne": si prende la RIGA
  // DI INTESTAZIONE della tabella (il frammento cliccato e le poche righe
  // subito sotto, dove le intestazioni vanno a capo) e se ne ricava una
  // "Colonna tabella" per ogni cella: la X viene da questo PDF, il nome dal
  // testo dell'intestazione (o dal nome già usato in un altro modello, se
  // combacia). Da rivedere/rinominare/togliere a mano dopo.
  function riconosciColonneDaRiga(frammento) {
    // 1) La riga dei titoli non è per forza esattamente quella cliccata:
    //    si prende, tra le righe vicine al click (±40pt), quella con PIÙ
    //    frammenti — è quasi sempre la riga di intestazione. Così un click
    //    un po' impreciso si autocorregge.
    const vicine = frammenti.filter((f) => Math.abs(f.y - frammento.y) <= 40);
    const perRiga = new Map();
    for (const f of vicine) {
      const k = Math.round(f.y / 3) * 3;
      perRiga.set(k, (perRiga.get(k) || 0) + 1);
    }
    let yTitolo = frammento.y;
    let maxN = -1;
    for (const [k, n] of perRiga) if (n > maxN) { maxN = n; yTitolo = k; }

    // 2) Banda verticale: dalla riga dei titoli fino a ~28pt più in basso
    //    (le intestazioni lunghe vanno a capo su 2-4 righe). In coordinate
    //    PDF la Y cresce verso l'alto, quindi "più in basso" = Y minore.
    const yAlto = yTitolo + 3;
    const yBasso = yTitolo - 28;
    const nonTitolo = (f) => f.testo.includes(" > ") || f.testo.trim().length > 35 || f.larghezza > 180;
    const nellaBanda = frammenti.filter((f) => f.y <= yAlto && f.y >= yBasso && !nonTitolo(f));
    if (nellaBanda.length === 0) return;

    // Raggruppa per X: frammenti con X vicine (≤14pt) sono la stessa colonna
    // (una su più righe = intestazione andata a capo). Testo concatenato
    // dall'alto in basso.
    const perX = [...nellaBanda].sort((a, b) => a.x - b.x);
    const gruppi = [];
    for (const f of perX) {
      const g = gruppi.find((gr) => Math.abs(gr.x - f.x) <= 14);
      if (g) { g.items.push(f); g.x = Math.min(g.x, f.x); }
      else gruppi.push({ x: f.x, items: [f] });
    }

    const nuove = [];
    for (const g of gruppi) {
      const testo = g.items
        .sort((a, b) => b.y - a.y || a.x - b.x)
        .map((f) => f.testo.trim())
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      if (!testo) continue;
      // Salta se troppo vicina a una colonna già definita (non la si duplica).
      if (colonneTabella.some((c) => Math.abs(c.x - g.x) <= 10)) continue;
      const canonico = vocabolarioMappa.get(normalizzaNome(testo));
      nuove.push({ nome: canonico || testo, x: g.x, y: g.items[0].y });
    }
    if (nuove.length === 0) {
      setMessaggioRiconoscimento("Nessuna colonna nuova trovata su questa riga — prova a cliccare più in alto, sulla riga dei titoli.");
      return;
    }

    // Nomi unici: se due celle danno lo stesso nome, si numerano (2), (3)...
    const visti = new Map();
    for (const col of nuove) {
      const n = visti.get(col.nome) || 0;
      visti.set(col.nome, n + 1);
      if (n > 0) col.nome = `${col.nome} (${n + 1})`;
    }

    const tutte = [...colonneTabella, ...nuove].sort((a, b) => a.x - b.x);
    setColonneTabella(tutte);

    // Indovina l'ancora se non ancora scelta: una colonna dal nome "noto"
    // (sequence/codice/riga/colore...), altrimenti la più a sinistra.
    if (!colonnaAncora) {
      const nota = tutte.find((c) => ANCORE_NOTE.has(normalizzaNome(c.nome)));
      setColonnaAncora((nota || tutte[0]).nome);
    }
    setMessaggioRiconoscimento(`Riconosciute ${nuove.length} colonne dalla riga di intestazione — controlla nomi e ancora qui sotto, poi togli quelle che non servono.`);
  }

  function apriPopup(frammento, elemento) {
    const rect = elemento.getBoundingClientRect();
    const contenitore = elemento.closest(".calibrazione-pagina").getBoundingClientRect();
    // In modalità "Nome posizione" il nome della colonna è quasi sempre lo
    // stesso: si pre-compila, l'utente preme Invio e via.
    setNomeCampo(modalita === "posizione" ? (anagraficaPosizioni?.nomeColonna || "Nome Posizione") : "");
    setPopup({ frammento, top: rect.bottom - contenitore.top + 4, left: rect.left - contenitore.left });
  }

  function confermaPopup() {
    if (!popup || !nomeCampo.trim()) return;
    if (modalita === "posizione") {
      setAnagraficaPosizioni({
        x: popup.frammento.x, y: popup.frammento.y,
        nomeColonna: nomeCampo.trim(), separatore: " ",
      });
      setPopup(null);
      return;
    }
    const voce = { nome: nomeCampo.trim(), x: popup.frammento.x, y: popup.frammento.y };
    if (modalita === "campo") setCampiPagina((prev) => [...prev.filter((c) => c.nome !== voce.nome), voce]);
    else setColonneTabella((prev) => [...prev.filter((c) => c.nome !== voce.nome), voce]);
    setPopup(null);
  }

  function rimuoviCampoPagina(nome) { setCampiPagina((prev) => prev.filter((c) => c.nome !== nome)); }

  // Un campo di pagina di norma si cerca da capo su OGNI pagina (es. il
  // codice articolo, ripetuto identico in cima a ciascuna). Alcuni PDF
  // invece hanno campi che compaiono una volta sola (es. un numero d'ordine
  // scritto solo in copertina): disattivando "ripetuto", il valore trovato
  // resta valido anche sulle pagine dove non ricompare, invece di svuotarsi
  // — vedi routes/pdfEstrattore.js estraiPagina.
  function alternaRipetizioneCampoPagina(nome) {
    setCampiPagina((prev) => prev.map((c) => (
      c.nome === nome ? { ...c, ripetuto: c.ripetuto === false ? true : false } : c
    )));
  }
  function rimuoviColonnaTabella(nome) {
    setColonneTabella((prev) => prev.filter((c) => c.nome !== nome));
    if (colonnaAncora === nome) setColonnaAncora("");
    setColonneObbligatorie((prev) => prev.filter((n) => n !== nome));
  }

  // Click sul foglio in modalità "Colonna variante": un solo click, sopra
  // l'intestazione che identifica quella variante, crea la colonna già
  // pronta — x/y del click fanno sia da confine colonna (per leggere il
  // valore in ogni riga materiale, come per qualunque colonna) sia da
  // posizione dell'intestazione stessa (l'intestazione stessa È il click:
  // qui non serve un secondo click separato). Nome automatico e progressivo,
  // il testo vero dell'intestazione non è il nome della colonna qui nello
  // strumento: è solo il valore che finirà nel campo "intestazione" in export.
  function creaColonnaVariante(frammento) {
    const n = colonneTabella.filter((c) => c.variante).length + 1;
    const nuova = {
      nome: `Variante ${n}`, x: frammento.x, y: frammento.y,
      variante: true, intestazioneX: frammento.x, intestazioneY: frammento.y,
    };
    setColonneTabella((prev) => [...prev, nuova].sort((a, b) => a.x - b.x));
  }

  function rimuoviVariante(nomeColonna) {
    setColonneTabella((prev) => prev.map((c) => (
      c.nome === nomeColonna ? { ...c, variante: undefined, intestazioneX: undefined, intestazioneY: undefined } : c
    )));
  }

  // Blocco taglie dinamico (routes/pdfEstrattore.js, calcolaBloccoTaglie):
  // alcuni PDF ripetono la stessa tabella su più pagine ma con la SCALA
  // TAGLIE diversa da un articolo all'altro (es. donna 35-42 su una
  // pagina, uomo 39-46 su un'altra, pure con un numero di colonne
  // diverso) — colonne calibrate a nome/posizione fissi catturerebbero la
  // taglia sbagliata appena la scala cambia. Marcando insieme più colonne
  // (di norma quelle create in blocco con "🔍 riconosci" sulla riga
  // taglie) si dice al motore di ricalcolare, pagina per pagina, quali
  // taglie ci sono davvero leggendo di nuovo l'intestazione — nessun
  // click sul foglio in più, solo un interruttore su colonne già create.
  function alternaTaglia(nomeColonna) {
    setColonneTabella((prev) => prev.map((c) => (
      c.nome === nomeColonna ? { ...c, taglia: c.taglia ? undefined : true } : c
    )));
  }

  // Punteggio di riconoscimento (routes/pdfModelli.js, calcolaPunteggioModello):
  // per chi non può mappare, "Applica modello" resta bloccato finché il
  // punteggio non è ESATTAMENTE 100% (Home.jsx, applicaModelloBloccato) — non
  // basta un'alta corrispondenza, serve perfetta. Alcune distinte stampano
  // UNA sola intestazione (es. "Composant") anche quando conviene calibrarla
  // come più colonne separate (codice, descrizione...), più eventuali
  // colonne puramente tecniche usate solo per delimitare un confine — quelle
  // non hanno mai un'etichetta di stampa propria da ritrovare e terrebbero il
  // modello bloccato sotto al 100% per sempre, anche a estrazione corretta.
  // Qui si escludono dal conteggio.
  function alternaEscludiPunteggio(nomeColonna) {
    setColonneTabella((prev) => prev.map((c) => (
      c.nome === nomeColonna ? { ...c, escludiDaPunteggio: c.escludiDaPunteggio ? undefined : true } : c
    )));
  }

  const paginaEsclusa = pagineEscluse.includes(numeroPagina);

  function alternaPaginaCorrente() {
    setPagineEscluse((prev) => (
      prev.includes(numeroPagina)
        ? prev.filter((n) => n !== numeroPagina)
        : [...prev, numeroPagina].sort((a, b) => a - b)
    ));
  }

  // I nomi che compaiono davvero nel risultato finale: una colonna marcata
  // "da dividere" ne produce due (le sue due parti) e sparisce col nome
  // originale — è su questi nomi che si ragiona per escludere una riga,
  // stessi nomi che il motore controlla davvero (routes/pdfEstrattore.js).
  const nomiColonneRisultato = colonneTabella.flatMap((c) => (
    c.dividi
      ? [c.dividi.nomeParte1, c.dividi.nomeParte2, ...(c.dividi.nomeParte3 ? [c.dividi.nomeParte3] : [])]
      : [c.nome]
  ));

  // Stessa idea, ma includendo anche i campi di pagina: è la lista fra cui
  // scegliere le "sorgenti" quando si costruisce un campo combinato — un
  // campo combinato può a sua volta unire un campo di pagina e uno di
  // tabella, o due dello stesso tipo.
  const nomiCampiDisponibili = [
    ...campiPagina.map((c) => c.nome),
    ...nomiColonneRisultato,
  ];

  function aggiungiCombinazione() {
    const { nome, sorgenti, separatore } = nuovaCombinazione;
    if (!nome.trim() || sorgenti.some((s) => !s)) return;
    setCampiCombinati((prev) => [...prev, { nome: nome.trim(), sorgenti: [...sorgenti], separatore }]);
    setNuovaCombinazione({ nome: "", sorgenti: ["", ""], separatore: "-" });
  }

  function rimuoviCombinazione(nome) {
    setCampiCombinati((prev) => prev.filter((c) => c.nome !== nome));
  }

  // Non solo due sorgenti: il motore (routes/pdfEstrattore.js
  // aggiungiCampiCombinati) unisce già un array di qualunque lunghezza con
  // lo stesso separatore — qui si aggiunge/toglie uno slot di scelta alla
  // volta nel form, restando sempre almeno a due.
  function aggiungiCampoSorgente() {
    setNuovaCombinazione((prev) => ({ ...prev, sorgenti: [...prev.sorgenti, ""] }));
  }

  function rimuoviCampoSorgente(indice) {
    setNuovaCombinazione((prev) => (
      prev.sorgenti.length <= 2 ? prev : { ...prev, sorgenti: prev.sorgenti.filter((_, i) => i !== indice) }
    ));
  }

  function impostaCampoSorgente(indice, valore) {
    setNuovaCombinazione((prev) => ({
      ...prev, sorgenti: prev.sorgenti.map((s, i) => (i === indice ? valore : s)),
    }));
  }

  function applicaDivisioneValore(valore, dividi) {
    const testoDiv = dividi.separatore === "\n" ? valore : valore.replace(/\n/g, " ");
    const pezzi = testoDiv.split(dividi.separatore).map((p) => p.trim()).filter((p) => p !== "");
    const taglio = Math.max(1, Math.min(dividi.indiceTaglio || 1, pezzi.length));
    const indiceFine = Number(dividi.indiceFine);
    const fine = Number.isFinite(indiceFine) && dividi.indiceFine != null
      ? Math.max(taglio, Math.min(indiceFine, pezzi.length)) : pezzi.length;
    return {
      [dividi.nomeParte1]: pezzi.slice(0, taglio).join(" ").trim(),
      [dividi.nomeParte2]: pezzi.slice(taglio, fine).join(" ").trim(),
      ...(dividi.nomeParte3 ? { [dividi.nomeParte3]: pezzi.slice(fine).join(" ").trim() } : {}),
    };
  }

  // Le righe logiche della pagina che si sta guardando, ciascuna con il
  // valore che avrebbero davvero le colonne già definite — stessa identica
  // logica di raggruppamento del motore (routes/pdfEstrattore.js), qui in
  // JS lato client per poter reagire subito al click, senza aspettare il
  // server. Restituisce anche i frammenti di testo di ogni riga, per poter
  // capire su quale riga si è cliccato e per colorarla sulla pagina.
  function calcolaRigheLogichePagina() {
    if (!colonnaAncora || colonneTabella.length === 0) return [];
    const colonne = [...colonneTabella].sort((a, b) => a.x - b.x).map((c, i, arr) => ({
      ...c,
      xMin: i === 0 ? -Infinity : (arr[i - 1].x + c.x) / 2,
      xMax: i === arr.length - 1 ? Infinity : (c.x + arr[i + 1].x) / 2,
    }));
    const trovaColonna = (x) => colonne.find((c) => x >= c.xMin && x < c.xMax) || colonne[colonne.length - 1];

    const yIntestazione = Math.min(...colonneTabella.map((c) => c.y));
    const sotto = frammenti.filter((f) => f.y < yIntestazione && (limiteInferioreTabella == null || f.y > limiteInferioreTabella));
    const ordinati = [...sotto].sort((a, b) => b.y - a.y || a.x - b.x);
    const righeVisive = [];
    for (const it of ordinati) {
      let riga = righeVisive.find((r) => Math.abs(r.y - it.y) <= (tolleranzaRigaVisiva ?? 2));
      if (!riga) { riga = { y: it.y, items: [] }; righeVisive.push(riga); }
      riga.items.push(it);
    }
    righeVisive.sort((a, b) => b.y - a.y);

    const righeLogiche = [];
    let corrente = null;
    let chiusa = false;
    for (const rigaVisiva of righeVisive) {
      const haAncora = rigaVisiva.items.some((it) => trovaColonna(it.x).nome === colonnaAncora);
      if (haAncora) { corrente = { righeVisive: [] }; righeLogiche.push(corrente); chiusa = false; }
      if (!corrente || chiusa) continue;
      const haMarker = testoFineRiga && rigaVisiva.items.some((it) => it.testo.trim() === testoFineRiga || it.testo.trim().startsWith(`${testoFineRiga} `));
      if (haMarker) { chiusa = true; continue; }
      corrente.righeVisive.push(rigaVisiva);
    }

    return righeLogiche.map((rl) => {
      const grezzo = {};
      for (const col of colonne) grezzo[col.nome] = [];
      for (const rigaVisiva of rl.righeVisive) {
        for (const it of rigaVisiva.items) grezzo[trovaColonna(it.x).nome].push(it.testo);
      }
      const valori = {};
      for (const col of colonne) {
        const testo = grezzo[col.nome].join(" ").trim();
        if (col.dividi && col.dividi.separatore) Object.assign(valori, applicaDivisioneValore(testo, col.dividi));
        else valori[col.nome] = testo;
      }
      return { righeVisive: rl.righeVisive, valori };
    });
  }

  function rigaEsclusa(valori) {
    return colonneObbligatorie.some((nome) => !(valori[nome] || "").trim());
  }

  // Click su un frammento in modalità "Escludi riga": si guardano le
  // colonne già definite ma VUOTE in questa riga (di solito è così che si
  // riconosce un titolo di sezione o un contatore) e si sceglie quella
  // compilata nel maggior numero di altre righe di questa pagina — così si
  // scarta la riga cliccata rischiando di buttarne via il meno possibile
  // di buone.
  function escludiRigaDelFrammento(frammento) {
    const righe = calcolaRigheLogichePagina();
    const riga = righe.find((r) => r.righeVisive.some((rv) => rv.items.includes(frammento)));
    if (!riga) return;
    if (rigaEsclusa(riga.valori)) { setErrore(""); return; } // già esclusa da una regola precedente
    const vuote = nomiColonneRisultato.filter((nome) => !(riga.valori[nome] || "").trim());
    if (vuote.length === 0) {
      setErrore("Questa riga ha tutte le colonne compilate: non sembra una riga da scartare.");
      return;
    }
    let scelta = vuote[0];
    let coperturaMax = -1;
    for (const nome of vuote) {
      const copertura = righe.filter((r) => (r.valori[nome] || "").trim() !== "").length;
      if (copertura > coperturaMax) { coperturaMax = copertura; scelta = nome; }
    }
    setErrore("");
    setColonneObbligatorie((prev) => [...prev, scelta]);
  }

  // "Dividi in due" vale sia per una colonna della tabella sia per un campo
  // di pagina: anche un'intestazione può contenere due dati attaccati (es.
  // "641D20A3221 - RS-ROBE ND+PL-POP. NOEUDS" = codice + descrizione), e
  // spesso serve solo una delle due parti. Si tiene traccia di QUALE dei due
  // elenchi va aggiornato alla conferma.
  function apriDivisione(voce, tipo) { setColonnaInDivisione({ ...voce, tipo }); }

  function confermaDivisione(divisione) {
    // "Dividi" ed "estrazione" sono alternativi sullo stesso campo: si
    // azzera l'altro per non tenerne uno inutilizzato in giro.
    const aggiorna = (prev) => prev.map((c) => (c.nome === colonnaInDivisione.nome ? { ...c, dividi: divisione, estrazione: undefined } : c));
    if (colonnaInDivisione.tipo === "campo") setCampiPagina(aggiorna);
    else setColonneTabella(aggiorna);
    setColonnaInDivisione(null);
  }

  function rimuoviDivisione(nome, tipo) {
    const azzera = (prev) => prev.map((c) => (c.nome === nome ? { ...c, dividi: undefined } : c));
    if (tipo === "campo") setCampiPagina(azzera);
    else setColonneTabella(azzera);
  }

  // "Estrai una parte": alternativa a "dividi" per quando serve solo UN
  // valore preso da dentro il campo/colonna, non due (vedi commento sopra
  // lo stato campoInEstrazione).
  function apriEstrazione(voce, tipo) { setCampoInEstrazione({ ...voce, tipo }); }

  function confermaEstrazione(estrazione) {
    const aggiorna = (prev) => prev.map((c) => (c.nome === campoInEstrazione.nome ? { ...c, estrazione, dividi: undefined } : c));
    if (campoInEstrazione.tipo === "campo") setCampiPagina(aggiorna);
    else setColonneTabella(aggiorna);
    setCampoInEstrazione(null);
  }

  function rimuoviEstrazione(nome, tipo) {
    const azzera = (prev) => prev.map((c) => (c.nome === nome ? { ...c, estrazione: undefined } : c));
    if (tipo === "campo") setCampiPagina(azzera);
    else setColonneTabella(azzera);
  }

  // Esempio da mostrare nello strumento di divisione per un campo di
  // pagina: il valore che ha su QUESTA pagina, cioè il frammento di testo
  // più vicino alla posizione calibrata (stessa regola del motore).
  function calcolaEsempioCampoPagina(campo) {
    if (frammenti.length === 0) return [];
    let migliore = null;
    let distanzaMigliore = Infinity;
    for (const f of frammenti) {
      const d = Math.hypot(f.x - campo.x, f.y - campo.y);
      if (d < distanzaMigliore) { distanzaMigliore = d; migliore = f; }
    }
    return migliore && distanzaMigliore <= 60 ? [migliore.testo.trim()] : [];
  }

  // Ricostruisce, solo per l'anteprima nello strumento di divisione, i valori
  // veri già visibili in questa pagina per una colonna — stessa logica (a
  // coordinate) del motore di estrazione, semplificata: qui basta unire con
  // uno spazio, senza il caso speciale dei numeri spezzati dal wrap.
  function calcolaEsempiColonna(nomeColonna) {
    if (!colonnaAncora || colonneTabella.length === 0) return [];
    const colonne = [...colonneTabella].sort((a, b) => a.x - b.x).map((c, i, arr) => ({
      ...c,
      xMin: i === 0 ? -Infinity : (arr[i - 1].x + c.x) / 2,
      xMax: i === arr.length - 1 ? Infinity : (c.x + arr[i + 1].x) / 2,
    }));
    const trovaColonna = (x) => colonne.find((c) => x >= c.xMin && x < c.xMax) || colonne[colonne.length - 1];

    const yIntestazione = Math.min(...colonneTabella.map((c) => c.y));
    const sotto = frammenti.filter((f) => f.y < yIntestazione && (limiteInferioreTabella == null || f.y > limiteInferioreTabella));
    const ordinati = [...sotto].sort((a, b) => b.y - a.y || a.x - b.x);
    const righeVisive = [];
    for (const it of ordinati) {
      let riga = righeVisive.find((r) => Math.abs(r.y - it.y) <= (tolleranzaRigaVisiva ?? 2));
      if (!riga) { riga = { y: it.y, items: [] }; righeVisive.push(riga); }
      riga.items.push(it);
    }
    righeVisive.sort((a, b) => b.y - a.y);

    const righeLogiche = [];
    let corrente = null;
    let chiusa = false;
    for (const rigaVisiva of righeVisive) {
      const haAncora = rigaVisiva.items.some((it) => trovaColonna(it.x).nome === colonnaAncora);
      if (haAncora) { corrente = []; righeLogiche.push(corrente); chiusa = false; }
      if (!corrente || chiusa) continue;
      const haMarker = testoFineRiga && rigaVisiva.items.some((it) => it.testo.trim() === testoFineRiga || it.testo.trim().startsWith(`${testoFineRiga} `));
      if (haMarker) { chiusa = true; continue; }
      corrente.push(rigaVisiva);
    }

    // Testo di una colonna in una riga logica: i frammenti della stessa riga
    // visiva si uniscono con uno spazio, righe visive diverse con un vero a
    // capo — come fa il motore (routes/pdfEstrattore.js unisciPezziCella), così
    // "A capo" come separatore nel modal vede le stesse pillole del file finale.
    const testoColonna = (rigaLogica, nome) => rigaLogica
      .map((rigaVisiva) => rigaVisiva.items.filter((it) => trovaColonna(it.x).nome === nome).map((it) => it.testo).join(" ").trim())
      .filter((t) => t !== "")
      .join("\n");
    const nomiGrezzi = new Set(colonneTabella.map((c) => c.nome));
    // Le righe già escluse da "colonne obbligatorie" (es. un "Date: 27-02-26"
    // di intestazione pagina) non finiscono nel file: non sono esempi validi.
    const obbligatorieGrezze = colonneObbligatorie.filter((n) => nomiGrezzi.has(n));

    return righeLogiche
      .filter((rigaLogica) => obbligatorieGrezze.every((n) => testoColonna(rigaLogica, n) !== ""))
      .map((rigaLogica) => testoColonna(rigaLogica, nomeColonna))
      .filter((v) => v !== "");
  }

  function posizioneAssegnata(frammento) {
    return [...campiPagina, ...colonneTabella].find((c) => Math.abs(c.x - frammento.x) < 0.5 && Math.abs(c.y - frammento.y) < 0.5);
  }

  // Quali frammenti, su QUESTA pagina, appartengono a una riga già esclusa
  // dalla regola corrente — calcolato una volta per render (non per ogni
  // frammento) e usato per colorarli di rosso: si vede subito l'effetto di
  // un click su tutta la pagina, comprese le righe non ancora cliccate ma
  // già coperte dalla stessa regola.
  const righeLogichePagina = colonneObbligatorie.length > 0 ? calcolaRigheLogichePagina() : [];
  const frammentiEsclusi = new Set();
  for (const riga of righeLogichePagina) {
    if (rigaEsclusa(riga.valori)) {
      for (const rv of riga.righeVisive) for (const it of rv.items) frammentiEsclusi.add(it);
    }
  }

  async function calcolaAnteprima() {
    setErrore("");
    if (colonneTabella.length === 0 || !colonnaAncora) {
      setErrore("Definisci almeno una colonna tabella e scegli la colonna ancora prima di calcolare l'anteprima.");
      return;
    }
    setCalcolando(true);
    try {
      const risultato = await anteprimaCalibrazionePdf({
        pdfId,
        calibrazione: { campiPagina, colonneTabella, colonnaAncora, pagineEscluse, colonneObbligatorie, limiteInferioreTabella, testoFineRiga, tolleranzaRigaVisiva, taglieRigaDinamiche, campiCombinati, anagraficaPosizioni, variantiOrizzontali },
      });
      setAnteprima(risultato);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setCalcolando(false);
    }
  }

  async function salvaEUsa() {
    setErrore("");
    if (!nomeCliente.trim()) { setErrore("Inserisci il nome del cliente per salvare il modello."); return; }
    if (colonneTabella.length === 0 || !colonnaAncora) {
      setErrore("Definisci almeno una colonna tabella e scegli la colonna ancora prima di salvare.");
      return;
    }
    setSalvando(true);
    try {
      const modello = await salvaModelloPdf({
        id: modelloIdInModifica || undefined,
        cliente: nomeCliente.trim(),
        calibrazione: { campiPagina, colonneTabella, colonnaAncora, pagineEscluse, colonneObbligatorie, limiteInferioreTabella, testoFineRiga, tolleranzaRigaVisiva, taglieRigaDinamiche, campiCombinati, anagraficaPosizioni, variantiOrizzontali },
        modulo,
      });
      const risposta = await applicaModelloPdf(pdfId, modello.id);
      if (inOrdini) {
        // OrdiniMapping.jsx è una pagina unica: il risultato passa dal
        // canale dedicato del contesto, consumato una volta sola lì.
        // clientePdf serve solo lì per mostrare il flag "modello applicato".
        setRisultatoPdfOrdini({ ...risposta, clientePdf: nomeCliente.trim() });
        navigate("/ordini/mapping");
      } else {
        setTemplateAttivo(null);
        setMapping({});
        setColonneNascoste([]);
        setRigheMappate(null);
        setFileCaricato(risposta);
        // Non si va dritti su un mapping vuoto: si torna alla Home, dove lo
        // step "2. Scegli come mappare" (già presente per il flusso Excel e
        // per l'applicazione di un modello PDF esistente) fa scegliere un
        // template cliente già salvato invece di ripartire da zero.
        navigate(percorsoBase);
      }
    } catch (err) {
      setErrore(err.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="pagina">
      <section className="riquadro">
        <h2>Calibra il modello PDF</h2>
        <p className="testo-secondario">
          Clicca sui frammenti di testo direttamente sulla pagina per dire cosa sono. "Campo di pagina" per un valore
          che si ripete identico su tutto il foglio (es. codice articolo); "Colonna tabella" per una colonna della
          tabella materiali, cliccando sulla sua intestazione; "Escludi riga" per buttare via una riga che non è un
          materiale (un titolo di sezione, un contatore) — clicca su un punto qualunque di quella riga, il resto lo
          capisco da solo; "Limite area tabella" per i PDF con più tabelle impilate sulla stessa pagina, quando ne
          serve solo una — clicca la prima riga della tabella successiva per fermare la lettura prima di lei;
          "Fine riga" per quando tra una riga vera e la successiva c'è un blocco da buttare la cui altezza cambia
          ogni volta (es. un elenco di componenti sotto ogni riga d'ordine) — clicca il testo che segna dove inizia
          quel blocco (es. "Componente/i fornito/i:"), lo riconosco ovunque ricompaia. Il modello si salva per
          cliente e si riusa da solo sui PDF futuri con lo stesso layout.
          {" "}<strong>Per partire in fretta: "Riconosci colonne" — clicca una volta sulla riga dei titoli e creo tutte le
          colonne in blocco</strong>, poi correggi nomi e ancora. Se la tabella ha colonne che identificano una variante
          del prodotto finito (es. il colore da usare per ciascuna variante), scegli <strong>"🎨 Colonna variante"</strong> e
          clicca l'intestazione di ciascuna, una alla volta: le creo già pronte (numerate in automatico), ogni riga
          materiale uscirà separata per variante, pronta per essere divisa in file diversi come già succede per
          articolo/colore.
        </p>
        <div className="riga-controlli">
          <label>
            <input type="radio" name="modalita" checked={modalita === "colonna"} onChange={() => setModalita("colonna")} />
            {" "}Colonna tabella
          </label>
          <label>
            <input type="radio" name="modalita" checked={modalita === "campo"} onChange={() => setModalita("campo")} />
            {" "}Campo di pagina
          </label>
          <label title={!colonnaAncora ? "Definisci prima almeno una colonna tabella e la colonna ancora" : "Clicca su una riga che non è un materiale: capisco da solo qual è la colonna che la tradisce"}>
            <input
              type="radio"
              name="modalita"
              checked={modalita === "escludi"}
              onChange={() => setModalita("escludi")}
              disabled={!colonnaAncora}
            />
            {" "}🧹 Escludi riga
          </label>
          <label title="Per PDF con più tabelle impilate sulla stessa pagina: clicca la prima riga della tabella successiva (quella che NON ti serve) per fermare la lettura prima di lei, su ogni pagina.">
            <input
              type="radio"
              name="modalita"
              checked={modalita === "limite"}
              onChange={() => setModalita("limite")}
            />
            {" "}📏 Limite area tabella
          </label>
          <label title="Clicca il testo che segna l'inizio di un blocco da buttare (es. 'Componente/i fornito/i:'): lo riconosco ovunque ricompaia, anche ad altezze diverse su pagine diverse.">
            <input
              type="radio"
              name="modalita"
              checked={modalita === "fineRiga"}
              onChange={() => setModalita("fineRiga")}
            />
            {" "}🛑 Fine riga
          </label>
          <label title="Per le distinte con righe-titolo di sezione ('Material (1)', 'Internal Materials (2)'): clicca una di quelle righe — le righe materiale sotto prenderanno come nome posizione il titolo + un contatore ('Material 1', 'Internal Materials 1', ...).">
            <input
              type="radio"
              name="modalita"
              checked={modalita === "posizione"}
              onChange={() => setModalita("posizione")}
            />
            {" "}🏷️ Nome posizione
          </label>
          <label title="Clicca UNA VOLTA sulla riga di intestazione della tabella: creo in blocco tutte le colonne (X presa dal PDF, nome dall'intestazione o da come l'hai già chiamata in altri modelli). Poi correggi/togli quello che serve.">
            <input
              type="radio"
              name="modalita"
              checked={modalita === "riconosci"}
              onChange={() => setModalita("riconosci")}
            />
            {" "}🔎 Riconosci colonne
          </label>
          <label title="Per le distinte con colonne che identificano una variante/combinazione del prodotto finito (es. il colore da usare per quella variante): clicca l'intestazione di ciascuna, una alla volta — creo la colonna già pronta, numerata in automatico (Variante 1, Variante 2, ...). Il valore di ognuna finirà su una riga separata.">
            <input
              type="radio"
              name="modalita"
              checked={modalita === "variante"}
              onChange={() => setModalita("variante")}
            />
            {" "}🎨 Colonna variante
          </label>
          <span className="riga-controlli">
            Zoom foglio:
            <button onClick={() => setZoomManuale((z) => Math.max(1, Math.round((z - 0.25) * 100) / 100))} disabled={zoomManuale <= 1}>－</button>
            {Math.round(zoomManuale * 100)}%
            <button onClick={() => setZoomManuale((z) => Math.min(2.5, Math.round((z + 0.25) * 100) / 100))} disabled={zoomManuale >= 2.5}>＋</button>
          </span>
          <span className="testo-secondario">
            {numeroPagina > 1 && <button onClick={() => setNumeroPagina(1)} title="Vai alla prima pagina">⏮ Prima</button>}
            {" "}
            {numeroPagina > 1 && <button onClick={() => setNumeroPagina((p) => p - 1)}>◀ Pagina prec.</button>}
            {" "}Pagina {numeroPagina} / {totalePagine}{" "}
            {numeroPagina < totalePagine && <button onClick={() => setNumeroPagina((p) => p + 1)}>Pagina succ. ▶</button>}
            {" "}
            {numeroPagina < totalePagine && <button onClick={() => setNumeroPagina(totalePagine)} title="Vai all'ultima pagina">Ultima ⏭</button>}
          </span>
          <label title="Alcuni PDF ripetono la stessa tabella su una seconda pagina, o intercalano pagine di riepilogo: escludile per non ritrovarti righe doppie o incomplete.">
            <input type="checkbox" checked={!paginaEsclusa} onChange={alternaPaginaCorrente} />
            {" "}Usa questa pagina
          </label>
        </div>
        {pagineEscluse.length > 0 && (
          <p className="testo-secondario" style={{ marginBottom: 0 }}>
            Pagine escluse dall'import: <strong>{pagineEscluse.join(", ")}</strong>
            {" "}<button className="btn-link-danger" onClick={() => setPagineEscluse([])}>usa tutte le pagine</button>
          </p>
        )}
        {modalita === "riconosci" && (
          <p className="testo-secondario" style={{ marginBottom: 0 }}>
            🔎 Clicca <strong>una volta</strong> sulla riga di intestazione della tabella (dove ci sono i titoli delle colonne).
            {messaggioRiconoscimento && <> — <span style={{ color: "var(--cf-text-primary)" }}>{messaggioRiconoscimento}</span></>}
          </p>
        )}
        {modalita === "variante" && (
          <p className="testo-secondario" style={{ marginBottom: 0 }}>
            🎨 Clicca l'intestazione che identifica <strong>una</strong> variante/combinazione (es. "F0JZH - NERO ARGENTO") — creo la
            colonna già pronta. Ripeti per ogni altra colonna variante presente.
          </p>
        )}
        {errore && <p className="errore">{errore}</p>}
      </section>

      <section className="riquadro">
        {paginaEsclusa && (
          <p className="errore" style={{ marginTop: 0 }}>
            ⊘ Pagina {numeroPagina} esclusa dall'import: i suoi dati non finiranno nel file finale.
          </p>
        )}
        <div
          className="calibrazione-pagina"
          style={{
            position: "relative", display: "inline-block", border: "1px solid var(--cf-border)",
            opacity: paginaEsclusa ? 0.45 : 1,
          }}
        >
          <canvas ref={canvasRef} />
          {frammenti.map((f, i) => {
            const assegnato = posizioneAssegnata(f);
            const escluso = frammentiEsclusi.has(f);
            const oltreLimite = limiteInferioreTabella != null && f.y <= limiteInferioreTabella;
            const èMarkerFineRiga = testoFineRiga != null && f.testo.trim() === testoFineRiga;
            const èMarkerPosizione = anagraficaPosizioni != null
              && Math.abs(f.x - anagraficaPosizioni.x) < 0.5 && Math.abs(f.y - anagraficaPosizioni.y) < 0.5;
            // Solo mentre si usa lo strumento "Nome posizione": suggerisce a
            // colpo d'occhio le righe-titolo di sezione cliccabili (testo che
            // finisce con "(numero)").
            const pareTitoloSezione = modalita === "posizione" && /\(\s*\d+\s*\)\s*$/.test(f.testo.trim());
            const left = f.x * scala;
            const top = (altezzaPagina - f.y - f.altezza) * scala;
            return (
              <div
                key={i}
                onClick={(e) => {
                  if (modalita === "escludi") escludiRigaDelFrammento(f);
                  else if (modalita === "limite") impostaLimiteInferiore(f);
                  else if (modalita === "fineRiga") impostaTestoFineRiga(f);
                  else if (modalita === "riconosci") riconosciColonneDaRiga(f);
                  else if (modalita === "variante") creaColonnaVariante(f);
                  else apriPopup(f, e.currentTarget);
                }}
                title={
                  modalita === "escludi" ? (escluso ? "Riga già esclusa" : "Clicca per escludere questa riga")
                    : modalita === "limite" ? "Clicca per fermare qui la lettura della tabella (questa riga e tutto sotto verranno ignorati)"
                    : modalita === "fineRiga" ? (èMarkerFineRiga ? "Marcatore di fine riga" : "Clicca per marcare qui l'inizio del blocco da buttare")
                    : modalita === "posizione" ? (èMarkerPosizione ? "Riga-titolo di sezione: dà il nome posizione alle righe sotto" : "Clicca una riga-titolo di sezione (es. 'Material (1)')")
                    : modalita === "riconosci" ? "Clicca sulla riga di intestazione della tabella: creo tutte le colonne in blocco"
                    : modalita === "variante" ? (assegnato?.variante ? `Colonna variante: ${assegnato.nome}` : "Clicca per creare qui una nuova colonna variante")
                    : f.testo
                }
                style={{
                  position: "absolute", left, top, width: f.larghezza * scala, height: f.altezza * scala,
                  cursor: "pointer",
                  background: èMarkerFineRiga ? "rgba(239,68,68,0.30)" : escluso ? "rgba(239,68,68,0.30)"
                    : èMarkerPosizione ? "rgba(34,197,94,0.35)" : pareTitoloSezione ? "rgba(34,197,94,0.15)"
                    : assegnato?.variante ? "rgba(217,70,239,0.35)"
                    : (assegnato ? "rgba(34,211,238,0.35)" : "transparent"),
                  border: èMarkerFineRiga ? "1px solid #ef4444" : escluso ? "1px solid #ef4444"
                    : èMarkerPosizione ? "1px solid #22c55e"
                    : assegnato?.variante ? "1px solid #d946ef"
                    : (assegnato ? "1px solid #22d3ee" : "1px solid transparent"),
                  opacity: oltreLimite ? 0.3 : 1,
                }}
              />
            );
          })}
          {limiteInferioreTabella != null && (
            <div
              title="Limite area tabella: sotto questa linea non viene più letto niente"
              style={{
                position: "absolute", left: 0, right: 0,
                top: (altezzaPagina - limiteInferioreTabella) * scala,
                borderTop: "2px dashed #f59e0b", pointerEvents: "none",
              }}
            />
          )}
          {popup && (
            <div
              style={{
                position: "absolute", top: popup.top, left: popup.left, zIndex: 10,
                background: "var(--cf-bg-card)", border: "1px solid var(--cf-border-strong)", borderRadius: 6,
                padding: "0.5rem", boxShadow: "0 4px 14px var(--cf-shadow)", display: "flex", gap: "0.4rem", alignItems: "center",
              }}
            >
              <input
                type="text"
                autoFocus
                list="vocabolario-colonne-pdf"
                placeholder={modalita === "campo" ? "es. Codice Articolo" : modalita === "posizione" ? "es. Nome Posizione" : "es. Net"}
                value={nomeCampo}
                onChange={(e) => setNomeCampo(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") confermaPopup(); if (e.key === "Escape") setPopup(null); }}
                style={{ width: "160px" }}
              />
              <datalist id="vocabolario-colonne-pdf">
                {vocabolario.map((v) => <option key={v.nome} value={v.nome} />)}
              </datalist>
              <button className="btn-primario" onClick={confermaPopup} disabled={!nomeCampo.trim()}>Aggiungi</button>
              <button onClick={() => setPopup(null)}>Annulla</button>
            </div>
          )}
        </div>
      </section>

      <section className="riquadro">
        <h3>Campi di pagina definiti ({campiPagina.length})</h3>
        {campiPagina.length === 0 && <p className="testo-secondario">Nessuno ancora — es. codice articolo, combinazione, colore.</p>}
        {campiPagina.length > 0 && (
          <div className="riga-controlli">
            {campiPagina.map((c) => (
              <span key={c.nome} className="riga-controlli" style={{ border: "1px solid var(--cf-border)", borderRadius: 4, padding: "0.2rem 0.5rem" }}>
                {c.nome}
                {c.dividi && (
                  <span className="testo-secondario"> → {c.dividi.nomeParte1} / {c.dividi.nomeParte2}{c.dividi.nomeParte3 ? ` / ${c.dividi.nomeParte3}` : ""}</span>
                )}
                {c.estrazione && <span className="testo-secondario"> (solo una parte)</span>}
                {" "}
                <label title="Se disattivato: questo campo compare una volta sola (es. in copertina) e il valore trovato viene riusato per le pagine successive dove non ricompare.">
                  <input
                    type="checkbox"
                    checked={c.ripetuto !== false}
                    onChange={() => alternaRipetizioneCampoPagina(c.nome)}
                  />
                  <span className="testo-secondario"> si ripete su ogni pagina</span>
                </label>
                {" "}
                <button onClick={() => apriEstrazione(c, "campo")} title="Estrai solo una parte (es. isolare il codice articolo dentro un titolo lungo)">🔍</button>
                {c.estrazione && <button className="btn-link-danger" onClick={() => rimuoviEstrazione(c.nome, "campo")} title="Annulla estrazione">annulla estr.</button>}
                {" "}
                <button onClick={() => apriDivisione(c, "campo")} title="Dividi in due (es. se qui ci sono codice e descrizione attaccati, ENTRAMBI utili)">✂️</button>
                {c.dividi && <button className="btn-link-danger" onClick={() => rimuoviDivisione(c.nome, "campo")} title="Annulla divisione">annulla div.</button>}
                {" "}
                <button className="btn-link-danger" onClick={() => rimuoviCampoPagina(c.nome)}>×</button>
              </span>
            ))}
          </div>
        )}

        <h3>Colonne tabella definite ({colonneTabella.length})</h3>
        {colonneTabella.length === 0 && <p className="testo-secondario">Nessuna ancora — clicca sulle intestazioni delle colonne della tabella materiali.</p>}
        {colonneTabella.length > 0 && (
          <div className="riga-controlli" style={{ flexWrap: "wrap" }}>
            {colonneTabella.map((c) => (
              <span key={c.nome} className="riga-controlli" style={{ border: "1px solid var(--cf-border)", borderRadius: 4, padding: "0.2rem 0.5rem" }}>
                {c.nome}
                {c.dividi && (
                  <span className="testo-secondario"> → {c.dividi.nomeParte1} / {c.dividi.nomeParte2}{c.dividi.nomeParte3 ? ` / ${c.dividi.nomeParte3}` : ""}</span>
                )}
                {c.estrazione && <span className="testo-secondario"> (solo una parte)</span>}
                {" "}
                <button onClick={() => apriEstrazione(c, "colonna")} title="Estrai solo una parte (serve solo un pezzo del valore, il resto si butta)">🔍</button>
                {c.estrazione && <button className="btn-link-danger" onClick={() => rimuoviEstrazione(c.nome, "colonna")} title="Annulla estrazione">annulla estr.</button>}
                {" "}
                <button onClick={() => apriDivisione(c, "colonna")} title="Dividi in due colonne (es. se qui finiscono due dati insieme, ENTRAMBI utili)">✂️</button>
                {c.dividi && <button className="btn-link-danger" onClick={() => rimuoviDivisione(c.nome, "colonna")} title="Annulla divisione">annulla div.</button>}
                {" "}
                {c.variante && (
                  <>
                    <span className="testo-secondario" title="Il valore di questa colonna, per ogni riga materiale, genera una riga separata per variante"> 🎨 variante</span>
                    {" "}
                    <button className="btn-link-danger" onClick={() => rimuoviVariante(c.nome)} title="Annulla: questa colonna torna a essere una colonna normale">annulla variante</button>
                    {" "}
                  </>
                )}
                {!c.variante && (
                  c.taglia ? (
                    <>
                      <span className="testo-secondario" title="Blocco taglie dinamico: su ogni pagina si rilegge quale taglia c'è davvero a questa posizione (la scala può cambiare da un articolo all'altro), invece di usare sempre questo nome"> 📏 taglia dinamica</span>
                      {" "}
                      <button className="btn-link-danger" onClick={() => alternaTaglia(c.nome)} title="Annulla: questa colonna torna a essere una colonna normale">annulla taglia dinamica</button>
                      {" "}
                    </>
                  ) : (
                    <>
                      <button onClick={() => alternaTaglia(c.nome)} title="Segna questa colonna come parte del blocco taglie dinamico: utile se la scala taglie (quali taglie, quante colonne) cambia da una pagina all'altra">📏 taglia dinamica</button>
                      {" "}
                    </>
                  )
                )}
                {c.escludiDaPunteggio ? (
                  <>
                    <span className="testo-secondario" title="Questa colonna non conta per il punteggio di riconoscimento automatico del layout: usalo per colonne tecniche senza una vera etichetta di stampa propria"> 🎯 esclusa dal punteggio</span>
                    {" "}
                    <button className="btn-link-danger" onClick={() => alternaEscludiPunteggio(c.nome)} title="Annulla: questa colonna torna a contare per il punteggio">annulla esclusione</button>
                    {" "}
                  </>
                ) : (
                  <>
                    <button onClick={() => alternaEscludiPunteggio(c.nome)} title="Escludi dal punteggio di riconoscimento automatico: usalo per colonne tecniche (es. un confine anti-collisione) senza una vera etichetta di stampa propria — senza questo, un layout corretto potrebbe restare bloccato sotto al 100% per chi non può mappare">🎯 escludi dal punteggio</button>
                    {" "}
                  </>
                )}
                <button className="btn-link-danger" onClick={() => rimuoviColonnaTabella(c.nome)}>×</button>
              </span>
            ))}
          </div>
        )}
        {colonneTabella.length > 0 && (
          <div style={{ marginTop: "0.5rem" }}>
            <label style={{ display: "block", marginBottom: "0.3rem" }}>Colonna ancora:</label>
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              La colonna che il programma usa per capire dove inizia ogni riga di materiale — deve essere una colonna
              SEMPRE compilata su ogni riga, mai vuota e mai spezzata su più righe (di solito la prima a sinistra).
              Serve perché nel PDF un testo lungo può andare a capo su più righe visive: senza un'ancora, il programma
              non saprebbe distinguere "continua la stessa riga" da "è iniziata una riga nuova".
            </p>
            <select value={colonnaAncora} onChange={(e) => setColonnaAncora(e.target.value)}>
              <option value="">-- scegli --</option>
              {colonneTabella.map((c) => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
            </select>

            <p className="testo-secondario" style={{ marginTop: "0.75rem", marginBottom: "0.3rem" }}>
              Se l'anteprima mostra righe "sfalsate" (il valore giusto sotto la colonna sbagliata, di solito con la
              colonna ancora spostata rispetto al resto della riga): alza questa tolleranza. Lasciala vuota per tutti
              gli altri casi.
            </p>
            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              Tolleranza allineamento riga (pt):
              <input
                type="number"
                min="2"
                max="10"
                step="0.5"
                style={{ width: "5rem" }}
                placeholder="2 (default)"
                value={tolleranzaRigaVisiva ?? ""}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setTolleranzaRigaVisiva(v === "" ? null : Number(v));
                }}
              />
            </label>
          </div>
        )}

        {colonneTabella.length > 0 && colonnaAncora && (
          <div style={{ marginTop: "1.25rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.3rem" }}>
              <input
                type="checkbox"
                checked={!!taglieRigaDinamiche}
                onChange={(e) => setTaglieRigaDinamiche(e.target.checked ? { offsetEtichette: 1, offsetValori: 2, campoSviluppo: "" } : null)}
              />
              🔢 Taglie dinamiche per riga
            </label>
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Per ordini dove OGNI riga articolo ha una propria mini-tabella taglie/quantità (numero di taglie diverso
              riga per riga, senza nessuna posizione in comune tra una riga e l'altra — diverso dal caso "una tabella
              con colonne taglia fisse che cambiano nome pagina per pagina", quello è "📏 taglia dinamica" sulle
              colonne). Le due righe si trovano contando quante righe di testo stanno sotto quella della colonna
              ancora: "1" = la riga subito sotto, "2" = due righe sotto, ecc.
            </p>
            {taglieRigaDinamiche && (
              <div className="riga-controlli" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  Etichette taglia: riga N.
                  <input
                    type="number"
                    min="1"
                    max="5"
                    style={{ width: "3.5rem" }}
                    value={taglieRigaDinamiche.offsetEtichette}
                    onChange={(e) => setTaglieRigaDinamiche((prev) => ({ ...prev, offsetEtichette: Number(e.target.value) || 1 }))}
                  />
                  sotto l'ancora
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  Valori taglia: riga N.
                  <input
                    type="number"
                    min="1"
                    max="5"
                    style={{ width: "3.5rem" }}
                    value={taglieRigaDinamiche.offsetValori}
                    onChange={(e) => setTaglieRigaDinamiche((prev) => ({ ...prev, offsetValori: Number(e.target.value) || 2 }))}
                  />
                  sotto l'ancora
                </label>
                <label
                  style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                  title="Alcuni PDF stampano, prima di tutte le taglie vere sulla riga etichette, un dato in più che varia riga per riga (es. il nome sviluppo dell'articolo) — non un'etichetta fissa da cercare per testo: si legge dinamicamente qualunque cosa contenga. Lascia vuoto se non c'è."
                >
                  Nome campo per il testo prima delle taglie (es. NomeSviluppo):
                  <input
                    type="text"
                    style={{ width: "10rem" }}
                    placeholder="lascia vuoto se non c'è"
                    value={taglieRigaDinamiche.campoSviluppo || ""}
                    onChange={(e) => setTaglieRigaDinamiche((prev) => ({ ...prev, campoSviluppo: e.target.value }))}
                  />
                </label>
              </div>
            )}
          </div>
        )}

        {colonneTabella.some((c) => c.variante) && (
          <div style={{ marginTop: "1.25rem" }}>
            <label style={{ display: "block", marginBottom: "0.3rem" }}>Colonne variante — nomi dei campi generati:</label>
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Ogni colonna segnata 🎨 fa uscire, per ogni riga materiale, una riga separata per ciascuna: il suo valore
              finisce nel campo sotto chiamato "valore", il testo dell'intestazione cliccata nel campo "intestazione"
              (cioè la variante/combinazione a cui appartiene). In revisione ed export questi file si separano già da
              soli per combinazione, come per qualunque altra Distinta Base.
            </p>
            <p className="riga-controlli">
              <label>
                Nome campo valore:{" "}
                <input
                  type="text"
                  value={variantiOrizzontali.campoValore}
                  onChange={(e) => setVariantiOrizzontali((prev) => ({ ...prev, campoValore: e.target.value }))}
                  placeholder="es. Colore Materiale"
                />
              </label>
              <label>
                Nome campo intestazione:{" "}
                <input
                  type="text"
                  value={variantiOrizzontali.campoIntestazione}
                  onChange={(e) => setVariantiOrizzontali((prev) => ({ ...prev, campoIntestazione: e.target.value }))}
                  placeholder="es. Nome Variante"
                />
              </label>
            </p>
          </div>
        )}

        {colonneTabella.length > 0 && (
          <div style={{ marginTop: "1.25rem" }}>
            <label style={{ display: "block", marginBottom: "0.3rem" }}>Righe da scartare:</label>
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Nella tabella finiscono anche righe che non sono materiali: titoli di sezione ("Material (1)",
              "Components (6)") o contatori ("Displaying 1 - 6 of 19 results"). Scegli "🧹 Escludi riga" qui sopra e
              clicca direttamente sul foglio, su una di quelle righe: si colora di rosso lei e tutte le altre righe
              come lei, sia in questa pagina che nelle prossime.
            </p>
            {colonneObbligatorie.length > 0 ? (
              <p className="riga-controlli">
                <span className="testo-secondario">
                  Scarto le righe senza <strong>{colonneObbligatorie.join(" e ")}</strong> compilata.
                </span>
                <button className="btn-link-danger" onClick={() => setColonneObbligatorie([])}>tieni tutte le righe</button>
              </p>
            ) : (
              <p className="testo-secondario">Per ora vengono tenute tutte le righe.</p>
            )}
          </div>
        )}

        {colonneTabella.length > 0 && (
          <div style={{ marginTop: "1.25rem" }}>
            <label style={{ display: "block", marginBottom: "0.3rem" }}>Limite area tabella:</label>
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Se sotto questa tabella, sulla stessa pagina, ce n'è un'altra (es. un riepilogo componenti) che rischia
              di essere letta per errore come se fosse la stessa tabella, scegli "📏 Limite area tabella" qui sopra e
              clicca sulla prima riga da NON considerare: tutto quello che sta a quella altezza o più in basso, su
              ogni pagina, viene ignorato.
            </p>
            {limiteInferioreTabella != null ? (
              <p className="riga-controlli">
                <span className="testo-secondario">
                  Ignoro tutto sotto la riga cliccata (Y = {Math.round(limiteInferioreTabella)}).
                </span>
                <button className="btn-link-danger" onClick={() => setLimiteInferioreTabella(null)}>rimuovi limite</button>
              </p>
            ) : (
              <p className="testo-secondario">Per ora nessun limite: viene letta tutta la pagina sotto l'intestazione.</p>
            )}
          </div>
        )}

        {colonneTabella.length > 0 && (
          <div style={{ marginTop: "1.25rem" }}>
            <label style={{ display: "block", marginBottom: "0.3rem" }}>Fine riga:</label>
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Se tra una riga vera e la successiva c'è un blocco da buttare la cui altezza cambia ogni volta (es. un
              elenco di componenti sotto ogni riga d'ordine, che non finisce sempre allo stesso punto della pagina),
              scegli "🛑 Fine riga" qui sopra e clicca sul testo che segna dove inizia quel blocco: lo riconosco
              ovunque ricompaia, anche ad altezze diverse su pagine diverse.
            </p>
            {testoFineRiga != null ? (
              <p className="riga-controlli">
                <span className="testo-secondario">
                  Da <strong>"{testoFineRiga}"</strong> in poi scarto tutto, fino alla prossima riga vera.
                </span>
                <button className="btn-link-danger" onClick={() => setTestoFineRiga(null)}>rimuovi marcatore</button>
              </p>
            ) : (
              <p className="testo-secondario">Per ora nessun marcatore: nessun blocco viene scartato in questo modo.</p>
            )}
          </div>
        )}

        {colonneTabella.length > 0 && (
          <div style={{ marginTop: "1.25rem" }}>
            <label style={{ display: "block", marginBottom: "0.3rem" }}>Nome posizione:</label>
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Se la distinta ha righe-titolo di sezione ("Material (1)", "Internal Materials (2)", "Components (6)")
              che indicano a quale posizione appartengono i materiali sotto, scegli "🏷️ Nome posizione" qui sopra e
              clicca una di quelle righe sul foglio: ogni riga materiale che la segue prende come nome posizione il
              titolo (senza il numero fra parentesi) più un contatore — <em>Material 1</em>, poi <em>Internal
              Materials 1</em>, <em>Internal Materials 2</em>, e così via. Il contatore riparte a ogni nuovo titolo e
              prosegue se la sezione continua sulla pagina dopo. Ne esce una colonna in più, da mappare poi nel
              template sul campo del tracciato che vuoi.
            </p>
            {anagraficaPosizioni ? (
              <p className="riga-controlli">
                <span className="testo-secondario">
                  Colonna <strong>{anagraficaPosizioni.nomeColonna}</strong> = titolo di sezione + contatore.
                </span>
                <button className="btn-link-danger" onClick={() => setAnagraficaPosizioni(null)}>rimuovi</button>
              </p>
            ) : (
              <p className="testo-secondario">Per ora nessuna: le righe-titolo di sezione, se ci sono, vengono solo scartate.</p>
            )}
          </div>
        )}

        {nomiCampiDisponibili.length >= 2 && (
          <div style={{ marginTop: "1.25rem" }}>
            <label style={{ display: "block", marginBottom: "0.3rem" }}>Unisci campi:</label>
            <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
              Se il valore vero non è un campo solo ma l'unione di due o più già definiti (es. il numero ordine
              cliente/commessa = numero d'ordine di pagina + numero di riga della tabella), scegline due o più qui
              sotto ("+ aggiungi campo" per una terza sorgente e oltre), un separatore, e il nome del campo nuovo:
              comparirà in più, accanto agli altri, nell'anteprima e nell'esportazione.
            </p>
            <p className="riga-controlli" style={{ flexWrap: "wrap" }}>
              {nuovaCombinazione.sorgenti.map((sorgente, indice) => (
                <span key={indice} className="riga-controlli" style={{ gap: "0.2rem" }}>
                  <select
                    value={sorgente}
                    onChange={(e) => impostaCampoSorgente(indice, e.target.value)}
                  >
                    <option value="">— campo {indice + 1} —</option>
                    {nomiCampiDisponibili.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
                  </select>
                  {nuovaCombinazione.sorgenti.length > 2 && (
                    <button
                      className="btn-link-danger"
                      onClick={() => rimuoviCampoSorgente(indice)}
                      title="Togli questo campo dalla combinazione"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              <button onClick={aggiungiCampoSorgente} title="Aggiungi un altro campo da unire (terzo, quarto, ...)">
                + aggiungi campo
              </button>
            </p>
            <p className="riga-controlli">
              <label>
                Separatore:{" "}
                <input
                  type="text"
                  value={nuovaCombinazione.separatore}
                  onChange={(e) => setNuovaCombinazione((prev) => ({ ...prev, separatore: e.target.value }))}
                  placeholder="separatore"
                  style={{ width: "4rem", textAlign: "center" }}
                  title='Testo messo fra ogni coppia di valori, es. "-". Vuoto per accostarli senza nulla in mezzo.'
                />
              </label>
              <span>➡</span>
              <input
                type="text"
                value={nuovaCombinazione.nome}
                onChange={(e) => setNuovaCombinazione((prev) => ({ ...prev, nome: e.target.value }))}
                placeholder="nome campo nuovo"
              />
              <button
                className="btn-primario"
                onClick={aggiungiCombinazione}
                disabled={!nuovaCombinazione.nome.trim() || nuovaCombinazione.sorgenti.some((s) => !s)}
              >
                Aggiungi combinazione
              </button>
            </p>
            {campiCombinati.length > 0 && (
              <div className="riga-controlli">
                {campiCombinati.map((c) => (
                  <span key={c.nome} className="riga-controlli" style={{ border: "1px solid var(--cf-border)", borderRadius: 4, padding: "0.2rem 0.5rem" }}>
                    <strong>{c.nome}</strong> = {c.sorgenti.join(` "${c.separatore}" `)}
                    {" "}
                    <button className="btn-link-danger" onClick={() => rimuoviCombinazione(c.nome)} title="Rimuovi">✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {colonnaInDivisione && (
        <DivisioneModal
          nomeColonna={colonnaInDivisione.nome}
          esempi={colonnaInDivisione.tipo === "campo"
            ? calcolaEsempioCampoPagina(colonnaInDivisione)
            : calcolaEsempiColonna(colonnaInDivisione.nome)}
          divisioneIniziale={colonnaInDivisione.dividi || null}
          onConferma={confermaDivisione}
          onAnnulla={() => setColonnaInDivisione(null)}
        />
      )}

      {campoInEstrazione && (
        <EstrazioneModal
          nomeColonnaSorgente={campoInEstrazione.nome}
          nomeColonnaDestinazione={campoInEstrazione.nome}
          esempi={campoInEstrazione.tipo === "campo"
            ? calcolaEsempioCampoPagina(campoInEstrazione)
            : calcolaEsempiColonna(campoInEstrazione.nome)}
          estrazioneIniziale={campoInEstrazione.estrazione || null}
          onConferma={confermaEstrazione}
          onAnnulla={() => setCampoInEstrazione(null)}
        />
      )}

      <section className="riquadro">
        <div className="riga-controlli">
          <button onClick={calcolaAnteprima} disabled={calcolando}>{calcolando ? "Calcolo..." : "Calcola anteprima"}</button>
          <label>Nome cliente:</label>
          <input type="text" value={nomeCliente} onChange={(e) => setNomeCliente(e.target.value)} placeholder="es. Céline" />
          <button className="btn-primario" onClick={salvaEUsa} disabled={salvando}>
            {salvando ? "Salvataggio..." : "Salva modello e continua"}
          </button>
          <button onClick={() => navigate(percorsoBase)}>Annulla</button>
        </div>
        {anteprima && (
          <>
            <p className="testo-secondario">
              {anteprima.totaleRighe} righe riconosciute su {anteprima.totalePagine - pagineEscluse.length} pagine
              {pagineEscluse.length > 0 && ` usate (${anteprima.totalePagine} in tutto, escluse ${pagineEscluse.join(", ")})`}
              {pagineEscluse.length === 0 && " del PDF"}.
              {colonneObbligatorie.length > 0 && ` Scartate le righe senza ${colonneObbligatorie.join(" / ")}.`}
            </p>
            <div className="scroll-orizzontale scroll-verticale">
              <table className="tabella-anteprima">
                <thead><tr>{anteprima.headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {anteprima.anteprima.map((r, i) => (
                    <tr key={i}>{anteprima.headers.map((h) => <td key={h}>{String(r[h] ?? "")}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
